---
title: "Common Beginner Mistakes and How to Avoid Them"
slug: common-beginner-mistakes
shortTitle: "Common Beginner Mistakes"
description: "Ten mistakes new traders make again and again, from trading without a stop loss to misreading lots and order types, with worked USDT examples and a simple fix for each."
level: beginner
category: trading
course: beginner
order: 14
topics: [risk-management, psychology]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/common-beginner-mistakes.svg
faqs:
  - q: "What is the most damaging beginner mistake?"
    a: "Risking too much on a single trade, usually by opening a position that is too large for the account and often without a stop loss. One oversized loss can undo weeks of careful trading."
  - q: "How much should a beginner risk per trade?"
    a: "There is no single right number, but many traders cap the loss on any one trade at 1 to 2% of their account. Whatever you choose, decide it before you open the trade and set the stop loss to match."
  - q: "Is using maximum leverage a mistake?"
    a: "Not in itself, but it makes it very easy to open a position far bigger than your account can handle. What matters is the size of the position compared with your equity, and where your stop loss is."
  - q: "Why did my stop loss close at a worse price than I set?"
    a: "A stop loss turns into a market order when the price reaches it. In a fast market or when a market opens with a gap, the next available price can be worse than your stop level. This is called slippage."
  - q: "Is it a mistake to add to a losing position?"
    a: "Adding to a loser, often called averaging down, increases your exposure on a trade that is already going against you. Without a written plan and a hard limit on total risk, it is one of the quicker routes to a large loss."
---

Almost every new trader makes the same handful of mistakes. That is actually good news: mistakes that are common are also predictable, and predictable mistakes can be prevented with a rule, a habit or a quick check before you press the button.

This lesson walks through ten of the most frequent ones, grouped into risk mistakes, order and platform mistakes, and mindset mistakes. Each comes with an illustrative example in USDT and a simple fix you can use on {{BRAND}} today.

<CTA />

<KeyTakeaways>

- **Most big losses start with size.** A position that is too large for the account, or has no stop loss, does more damage than any wrong market call.
- **Know exactly what each field means.** Amount is the position's value, a lot can be bigger than it sounds, and Limit or Stop depends on where you set the price.
- **Your trades share one pool of equity.** Under cross margin, several similar positions behave like one big one.
- **Process beats prediction.** A written plan, a journal and patience fix most of the mistakes on this list.

</KeyTakeaways>

## Risk mistakes

### 1. Trading without a stop loss

"I'll keep an eye on it" works until you look away. Without a stop loss, the only limit on a losing trade is your nerve, or the stop-out.

<Example title="With and without a stop (illustrative prices)">

You buy 0.1 BTC at 60,000. The price falls 5% to 57,000.

- Without a stop: (57,000 − 60,000) × 0.1 = **−300 USDT**, and still open.
- With a stop at 59,000: (59,000 − 60,000) × 0.1 = **−100 USDT**, closed and done.

</Example>

**Fix:** switch on **TP / SL** for every order and enter the most you will lose in **Loss (USDT)**.

### 2. Sizing from the slider instead of from your risk

The Amount slider's 100% mark means "the largest position my margin allows", not "a sensible position". On a 500 USDT account at 20x, the maximum is close to 500 × 20 = 10,000 USDT. A 2% move against that is 200 USDT, or 40% of the account.

**Fix:** start from the loss you accept, for example 1% of 500 = 5 USDT, then size the position so the stop loss costs exactly that. [Position Sizing](/academy/article/position-sizing) shows the math.

### 3. Misreading Amount and lots

In USDT mode, **Amount** is the position's full value, not the margin. In Lots mode, one lot can be much bigger than it sounds: one lot of gold is 100 oz.

<Example title="How big is one lot of gold? (illustrative prices)">

With gold at 2,400, one lot is 100 × 2,400 = **240,000 USDT** of exposure, and every 1 USDT move in the gold price changes your PnL by **100 USDT**. Even 0.1 lot is 24,000 USDT of exposure at 10 USDT per 1 USDT move.

</Example>

**Fix:** before you confirm, read **Value of $1 move** in the order summary, and in Lots mode the **Notional** line.

### 4. Moving the stop loss or averaging down

When a trade goes wrong, it is tempting to move the stop further away or add to the position at a "better" price. Both increase the loss you are exposed to on a trade that is already proving you wrong.

**Fix:** stops move in one direction only: towards protecting the trade.

### 5. Stacking trades that move together

Buying BTC, ETH and SOL at the same time can feel like three separate ideas. In practice, crypto prices often move together. If each position risks 20 USDT and all three stops are hit on the same sell-off, you lose 60 USDT on what was really one bet. Under {{BRAND}}'s cross margin, all three also draw on the same equity.

**Fix:** count correlated positions as one when you set your risk.

<Warning>

Oversized positions, missing stops and correlated trades combine badly. Leverage magnifies every one of them, and if your margin level reaches {{STOP_OUT_LEVEL}}, positions are closed automatically, starting with the largest loss.

</Warning>

## Order and platform mistakes

### 6. Mixing up Limit and Stop orders

On the **Limit / Stop** tab, the order type follows the price you type. With the Ask at 60,000, a buy at 59,000 becomes a **Buy Limit** (buy cheaper), while a buy at 61,000 becomes a **Buy Stop** (buy if the price breaks higher). Sells work the other way round. Beginners sometimes set a "limit" above the market and are surprised when it fills straight after a rally.

**Fix:** check the order type the panel shows, such as Buy Limit or Buy Stop, before you place it, and remember that a pending order reserves margin as soon as it is placed. [Order Types Explained](/academy/article/order-types-explained) covers each one.

### 7. Ignoring costs

Every trade pays the spread and {{TRADE_FEE}}. Positions held past {{SWAP_HOUR}} pay a daily swap, which on non-crypto markets is charged three times on {{SWAP_TRIPLE_DAY}}. Small costs add up quickly for frequent traders.

<Example title="How costs add up (hypothetical costs)">

Say each round trip costs 1.50 USDT in spread and fees. At 20 trades a day, that is 20 × 1.50 = **30 USDT a day**, or 30 × 20 = **600 USDT** over 20 trading days, before a single winning trade.

</Example>

**Fix:** check **Fee** in the order summary and read [Funding, Swaps and Trading Costs](/academy/article/trading-costs-swaps-fees).

### 8. Trading straight into big news

Interest-rate decisions, inflation data and company earnings can move prices sharply in seconds. Stop losses can slip, and stock and index markets can open with gaps.

**Fix:** check the economic calendar on the **Markets** page before you trade, and decide in advance whether to stay out or trade smaller.

## Mindset mistakes

### 9. Overtrading and revenge trading

Boredom leads to trades with no real reason; a loss leads to trades meant to "win it back". Both lower your standards and raise your costs. [Trading Psychology Basics](/academy/article/trading-psychology-basics) explains why.

**Fix:** set a daily loss limit and a maximum number of trades per day.

### 10. Skipping practice and record-keeping

Going live too big, too soon, and never writing anything down, means repeating the same mistakes without noticing.

**Fix:** practise on a demo account if one is available to you, start live trading with small sizes, and keep a simple journal of every trade.

<ProTip>

Make one habit non-negotiable: before every order, read **Margin required**, **Fee** and **Value of $1 move**. Those three numbers catch most of the mistakes above, including the wrong size, the wrong unit and an unexpected cost.

</ProTip>

## The ten mistakes at a glance

<CompareTable caption="Beginner mistakes and their fixes">

| Mistake | Quick fix |
| --- | --- |
| No stop loss | TP / SL on every order, with Loss (USDT) |
| Sizing from the slider | Size from the loss you accept |
| Misreading Amount or lots | Check Value of $1 move and Notional |
| Moving stops, averaging down | Stops only move to protect |
| Correlated positions | Count them as one risk |
| Limit and Stop mixed up | Check the order type shown first |
| Ignoring costs | Check Fee; know the swap |
| Trading into news | Check the economic calendar |
| Overtrading, revenge trading | Daily loss and trade limits |
| No practice, no journal | Demo first, small size, write it down |

</CompareTable>

## Conclusion

Beginner mistakes are rarely about picking the wrong market. They are about size, stops, misread fields, hidden costs and decisions made in the heat of the moment. Each has a simple fix: a stop loss on every trade, a position sized from the loss you accept, a quick read of the order summary, a look at the calendar, and a journal. Build those habits on small positions and most of the expensive lessons never need to happen.
